import { useState } from 'react'

import environment from 'environment'

import { CardType, EDH_FORMATS } from 'types/deck'
import { COLOR_MAP, COLOR_OBJECT } from 'components/deckPage/charts/charts.const'
import { ColorAbr } from 'services/apiTypes/card.types'

import { useAppSelector } from 'app-redux/hooks'
import { handleHighlight, handleMultiSelect } from 'app-redux/active/actions/multiSelectors'

import ManaSymbol from 'components/misc/customIcons/ManaSymbol'
import ColorBar from 'components/deckPage/charts/ColorBar'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import Icon from 'components/elements/Icon'

import { getCastingCostColors, getDeckColorIdent, getManaProductionData } from 'utils/mtgColor.utils'
import { CardSelectors } from 'utils/cardSelection'

import styles from './colorProductionVsGeneration.module.scss'

const ColorProductionVsGeneration = () => {
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const format = useAppSelector(state => state.deck.format)
  const categories = useAppSelector(state => state.deck.categories)

  const deck = Object.values(cardMap)
  const isCommanderFormat = EDH_FORMATS.includes(format)
  const deckColorIdentity: ColorAbr[] = getDeckColorIdent(deck, categories, isCommanderFormat)
  const hasColorlessCostInDeck = deck.find(card => card.castingCost.includes('C'))
  const colors: ColorAbr[] = ['W', 'U', 'B', 'R', 'G', 'C']

  const [landsOnly, setLandsOnly] = useState(false)
  const [showOutOfIdentity, setShowOutOfIdentity] = useState(!isCommanderFormat)
  const [includeColorlessProduction, setIncludeColorlessProduction] = useState(!!hasColorlessCostInDeck)

  const {
    colors: castingCostColorData,
    totalPips: totalCostPips,
    cardsInColor: cardsInColorCost,
  } = getCastingCostColors(deck, categories)

  const {
    colors: manaProductionData,
    totalPips: totalProductionPips,
    cardsInColor: cardsProduceColor,
  } = getManaProductionData(deck, categories, landsOnly, showOutOfIdentity ? undefined : deckColorIdentity)

  const coloredProduction = Object.keys(manaProductionData).reduce(
    (acc, color) => {
      // Skipping colorless producers
      if (color === 'C' && !includeColorlessProduction) return acc

      acc[color] = manaProductionData[color]

      return acc
    },
    {} as Record<string, number>,
  )

  return (
    <div className={styles.container}>
      <div className={styles.relativeColorBars}>
        <label>Cost</label>
        <ColorBar
          includeManaSymbols
          colors={castingCostColorData}
          className={styles.relativeColorBar}
          clickingBarFocuses="cost"
        />

        <label>
          Production
          <ArchidektDropdown
            menuClassName={styles.productionOptionsMenu}
            options={[
              {
                type: 'checkbox',
                checked: showOutOfIdentity,
                label: 'Show out of identity colors',
                onChange: () => setShowOutOfIdentity(!showOutOfIdentity),
              },
              {
                type: 'checkbox',
                checked: landsOnly,
                label: 'Use lands only for production',
                onChange: () => setLandsOnly(!landsOnly),
              },
              {
                type: 'checkbox',
                checked: includeColorlessProduction,
                label: 'Include colorless production',
                onChange: () => setIncludeColorlessProduction(!includeColorlessProduction),
              },
            ]}>
            <button className={styles.productionOptionsTrigger}>
              <Icon name="ellipsis horizontal" />
            </button>
          </ArchidektDropdown>
        </label>
        <ColorBar
          includeManaSymbols
          colors={coloredProduction}
          className={styles.relativeColorBar}
          clickingBarFocuses="production"
        />
      </div>
      <div className={styles.content}>
        {colors.map(color => (
          <ColorBreakdown
            key={color}
            castingCost={castingCostColorData[color]}
            totalCost={totalCostPips}
            manaProduction={manaProductionData[color]}
            totalProduction={totalProductionPips}
            colorAbr={color}
            cardsInColorColor={cardsInColorCost[color]}
            cardsInColorProduction={cardsProduceColor[color]}
          />
        ))}
      </div>
    </div>
  )
}

export default ColorProductionVsGeneration

const ColorBreakdown = ({
  castingCost,
  totalCost,
  manaProduction,
  totalProduction,
  colorAbr,
  cardsInColorColor,
  cardsInColorProduction,
}: {
  castingCost: number
  totalCost: number
  manaProduction: number
  totalProduction: number
  colorAbr: ColorAbr
  cardsInColorColor: number
  cardsInColorProduction: number
}) => {
  const handleSelectionChoice = (e: React.MouseEvent, cards: CardType[]) => {
    const modKeyHeld = environment.getOnMac() ? e.metaKey : e.ctrlKey

    return modKeyHeld ? handleMultiSelect(cards) : handleHighlight(cards)
  }

  const handleSelectColor = (e: React.MouseEvent) => handleSelectionChoice(e, CardSelectors.smartColor(colorAbr))
  const handleSelectCost = (e: React.MouseEvent) => handleSelectionChoice(e, CardSelectors.cost(colorAbr))
  const handleSelectProducers = (e: React.MouseEvent) => handleSelectionChoice(e, CardSelectors.producers(colorAbr))

  const hasNoCastingCost = !castingCost
  const hasNoData = !castingCost && !manaProduction

  return (
    <div
      className={`
      ${styles.colorBreakdown} 
      ${hasNoData ? styles.hasNoData : ''}
      ${hasNoCastingCost ? styles.hasNoCost : ''}
    `}>
      <button className={styles.colorbutton} onClick={handleSelectColor}>
        <ManaSymbol symbol={colorAbr} className={styles.manaSymbols} />
      </button>
      <div className={styles.bars}>
        <Bar label="Cost" fill={castingCost / totalCost || 0} color={colorAbr} onClick={handleSelectCost} />
        <div className={styles.extras}>
          {castingCost} pip{castingCost !== 1 ? 's' : ''} - {cardsInColorColor} card{cardsInColorColor !== 1 ? 's' : ''}
        </div>
        <Bar
          label="Production"
          fill={manaProduction / totalProduction || 0}
          color={colorAbr}
          onClick={handleSelectProducers}
        />
        <div className={styles.extras}>
          {manaProduction} mana - {cardsInColorProduction} card{cardsInColorProduction !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  )
}

const Bar = ({
  fill,
  label,
  color,
  onClick,
}: {
  fill: number
  label: string
  color: ColorAbr
  onClick: (e: React.MouseEvent) => void
}) => {
  const fillAsPercent = Math.round(100 * fill)
  const backgroundColor = COLOR_OBJECT[COLOR_MAP[color]]

  return (
    <div className={styles.barContainer}>
      <label>{label}</label>
      <button className={styles.bar} onClick={onClick}>
        <div className={styles.fill} style={{ width: `${fillAsPercent}%`, backgroundColor }} />
        <div className={styles.barText}>{fillAsPercent}%</div>
      </button>
    </div>
  )
}
